import { HttpErrorResponse } from "@angular/common/http";

import { FinancialInstitute } from "@core/models/financial-insitute.model";
import { Intermediary } from "@core/models/intermediary.model";
import { TPagination } from "@custom-types/sorting";
import { AccountManager } from "@models/account-manager.model";
import { Advisor } from "@models/advisor.model";
import { Communication } from "@models/communication.model";
import { Country } from "@models/country.model";
import { Document } from "@models/document.model";
import { Email } from "@models/email.model";
import { Etf } from "@models/etf-model";
import { Insurer } from "@models/insurer.model";
import { createReducer, on } from "@ngrx/store";

import * as ManagementActions from "../store/management.actions";

export const MANAGEMENT_FEATURE_KEY = "management";

export interface ManagementState {
  advisors: Array<Advisor>;
  insurers: Array<Insurer>;
  communication: Communication;
  accountManagers: Array<AccountManager>;
  financialInstitutions: Array<FinancialInstitute>;
  intermediaries: Array<Intermediary>;
  countries: Array<Country>;
  etfList: Array<Etf>;
  pagination: Partial<TPagination> | undefined;
  documentsPagination: Partial<TPagination> | undefined;
  listLength: number;
  documentsLength: number;
  valid: boolean;
  loading: boolean;
  emailsLoading: boolean;
  documentsLoading: boolean;
  financialInstitutionsLoading: boolean;
  lastActionDate: Date;
  error: HttpErrorResponse | null;
  emailExists: boolean;
  usernameExists: boolean;
}

const initialState: ManagementState = {
  advisors: [],
  insurers: [],
  accountManagers: [],
  financialInstitutions: [],
  intermediaries: [],
  communication: { emails: [], documents: [], email: null },
  countries: [],
  etfList: [],
  pagination: undefined,
  documentsPagination: undefined,
  listLength: 0,
  documentsLength: 0,
  valid: true,
  loading: false,
  emailsLoading: false,
  documentsLoading: false,
  financialInstitutionsLoading: false,
  lastActionDate: new Date(),
  error: null,
  emailExists: false,
  usernameExists: false,
};

export const MANAGEMENT_REDUCER = createReducer(
  initialState,
  on(
    ManagementActions.SET_ADVISORS,
    (
      state: ManagementState,
      action: { advisorList: Array<Advisor> }
    ): ManagementState => {
      return {
        ...state,
        advisors: action.advisorList,
      };
    }
  ),
  on(
    ManagementActions.SET_INSURERS,
    (
      state: ManagementState,
      action: { insurerList: Array<Insurer> }
    ): ManagementState => {
      return {
        ...state,
        insurers: action.insurerList,
      };
    }
  ),
  on(
    ManagementActions.SET_ACCOUNT_MANAGERS,
    (
      state: ManagementState,
      action: { accountManagerList: Array<AccountManager> }
    ): ManagementState => {
      return {
        ...state,
        accountManagers: action.accountManagerList,
      };
    }
  ),
  on(
    ManagementActions.SET_MANAGEMENT_LOADING_STATUS,
    (state: ManagementState, action: { loading: boolean }): ManagementState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),
  on(
    ManagementActions.SET_EMAILS,
    (
      state: ManagementState,
      action: { emailList: Array<Email> }
    ): ManagementState => {
      return {
        ...state,
        communication: { ...state.communication, emails: action.emailList },
      };
    }
  ),
  on(
    ManagementActions.SET_DOCUMENTS,
    (
      state: ManagementState,
      action: { documentList: Array<Document> }
    ): ManagementState => {
      return {
        ...state,
        communication: {
          ...state.communication,
          documents: action.documentList,
        },
      };
    }
  ),
  on(
    ManagementActions.SET_EMAILS_LOADING_STATUS,
    (state: ManagementState, action: { loading: boolean }): ManagementState => {
      return {
        ...state,
        emailsLoading: action.loading,
      };
    }
  ),
  on(
    ManagementActions.SET_DOCUMENTS_LOADING_STATUS,
    (state: ManagementState, action: { loading: boolean }): ManagementState => {
      return {
        ...state,
        documentsLoading: action.loading,
      };
    }
  ),
  on(
    ManagementActions.SET_LAST_ACTION_DATE,
    (state: ManagementState, action: { date: Date }): ManagementState => {
      return {
        ...state,
        lastActionDate: action.date,
      };
    }
  ),
  on(
    ManagementActions.SET_MANAGEMENT_ERROR,
    (
      state: ManagementState,
      action: { error: HttpErrorResponse }
    ): ManagementState => {
      return {
        ...state,
        error: action.error,
      };
    }
  ),
  on(
    ManagementActions.SET_FINANCIAL_INSTITUTIONS,
    (
      state: ManagementState,
      action: { financialInstituteList: Array<FinancialInstitute> }
    ): ManagementState => {
      return {
        ...state,
        financialInstitutions: action.financialInstituteList,
      };
    }
  ),
  on(
    ManagementActions.SET_INTERMEDIARIES,
    (
      state: ManagementState,
      action: { intermediaryList: Array<Intermediary> }
    ): ManagementState => {
      return {
        ...state,
        intermediaries: action.intermediaryList,
      };
    }
  ),
  on(
    ManagementActions.SET_COUNTRIES,
    (
      state: ManagementState,
      action: { countryList: Array<Country> }
    ): ManagementState => {
      const filter = [...action.countryList];
      const sortedCountryList = filter.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      return {
        ...state,
        countries: sortedCountryList,
      };
    }
  ),
  on(
    ManagementActions.SET_EMAIL,
    (state: ManagementState, action: { email: Email }): ManagementState => {
      return {
        ...state,
        communication: { ...state.communication, email: action.email },
      };
    }
  ),
  on(
    ManagementActions.SET_ETF_LIST,
    (
      state: ManagementState,
      action: { etfList: Array<Etf> }
    ): ManagementState => {
      return {
        ...state,
        etfList: action.etfList,
      };
    }
  ),
  on(
    ManagementActions.SET_FUND_ISIN_VALIDITY,
    (state: ManagementState, action: { valid: boolean }): ManagementState => {
      return {
        ...state,
        valid: action.valid,
      };
    }
  ),
  on(
    ManagementActions.SET_EMAIL_VALIDITY,
    (
      state: ManagementState,
      {
        emailExists,
      }: {
        emailExists: boolean;
      }
    ): ManagementState => {
      return {
        ...state,
        emailExists,
      };
    }
  ),
  on(
    ManagementActions.SET_USERNAME_VALIDITY,
    (
      state: ManagementState,
      {
        usernameExists,
      }: {
        usernameExists: boolean;
      }
    ): ManagementState => {
      return {
        ...state,
        usernameExists,
      };
    }
  ),
  on(
    ManagementActions.SET_MANAGEMENT_PAGINATION,
    (
      state: ManagementState,
      action: { pagination: Partial<TPagination> | undefined }
    ): ManagementState => {
      return {
        ...state,
        pagination: action.pagination,
      };
    }
  ),
  on(
    ManagementActions.SET_DOCUMENTS_PAGINATION,
    (
      state: ManagementState,
      action: { pagination: Partial<TPagination> | undefined }
    ): ManagementState => {
      return {
        ...state,
        documentsPagination: action.pagination,
      };
    }
  ),
  on(
    ManagementActions.SET_MANAGEMENT_LIST_LENGTH,
    (state: ManagementState, action: { length: number }): ManagementState => {
      return {
        ...state,
        listLength: action.length,
      };
    }
  ),
  on(
    ManagementActions.SET_DOCUMENTS_LIST_LENGTH,
    (state: ManagementState, action: { length: number }): ManagementState => {
      return {
        ...state,
        documentsLength: action.length,
      };
    }
  ),
  on(
    ManagementActions.SET_FINANCIAL_INSTITUTIONS_LOADING,
    (state: ManagementState, action: { loading: boolean }): ManagementState => {
      return {
        ...state,
        financialInstitutionsLoading: action.loading,
      };
    }
  )
);
